import type { ProductSource } from '#shared/types/core/common'
import type {
  CartCheckoutRequest,
  CartDto,
  CartItemDto,
} from '#shared/types/core/ecommerce/cart'
import type { CheckoutResponse } from '#shared/types/core/ecommerce/checkout'

export function useCartApi() {
  const id = useState<UUID | undefined>('cartId', () => undefined)
  const api = useCoreApi('ecommerce/carts')

  return {
    async getActiveOrNewCart(productSource?: ProductSource) {
      const cart = await api.$fetch<CartDto>('/', {
        query: {
          productSource,
        },
      })
      if (cart) {
        id.value = cart?.uuid
      }
      return cart ?? null
    },

    async deleteCart() {
      return await api.$fetch<void>(api.endpoint(unref(id)), {
        method: 'DELETE',
      })
    },

    async addItemToCart(item: Omit<CartItemDto, 'uuid'>) {
      return await api.$fetch<CartDto>(api.endpoint(unref(id), 'item'), {
        method: 'POST',
        body: item,
      })
    },

    async deleteItemFromCart(itemId: UUID) {
      return await api.$fetch<CartDto>(
        api.endpoint(unref(id), 'item', itemId),
        {
          method: 'DELETE',
        },
      )
    },

    async updateCartItem({ uuid: itemId, ...item }: CartItemDto) {
      return await api.$fetch<CartItemDto>(
        api.endpoint(unref(id), 'item', itemId),
        {
          method: 'PUT',
          body: item,
        },
      )
    },

    async startSubscriptions(request: CartCheckoutRequest) {
      if (request.paymentSource !== 'INVOICE') {
        throw new Error('Only INVOICE is supported at the moment')
      }

      return await api.$fetch<void>(
        api.endpoint(unref(id), 'start-subscriptions'),
        {
          method: 'POST',
          body: request,
        },
      )
    },

    /**
     * Invoice checkout for organization carts that contain at least one item
     * without a subscription plan (mixed or regular-only carts).
     */
    async hybridCheckout(request: CartCheckoutRequest) {
      if (request.paymentSource !== 'INVOICE' || request.customerType !== 'ORGANIZATION') {
        throw new Error('Hybrid checkout only supports INVOICE payments for organizations')
      }

      return await api.$fetch<CheckoutResponse>(
        api.endpoint(unref(id), 'hybrid-checkout'),
        {
          method: 'POST',
          body: request,
        },
      )
    },

    async checkout({
      redirectUrl,
      ...request
    }: CartCheckoutRequest & {
      redirectUrl: string
    }) {
      if (request.paymentSource !== 'VIPPS') {
        throw new Error('Only VIPPS is supported at the moment')
      }

      return await api.$fetch<CheckoutResponse>(api.endpoint(unref(id), 'checkout'), {
        method: 'POST',
        body: request,
        query: {
          redirectUrl,
        },
      })
    },
  }
}
